import { useCallback, useMemo } from 'react'

import { useConnect } from 'redux-bundler-hook'

import {
  BookOutlined,
  BuildCircleOutlined,
  DateRangeRounded,
  Refresh,
  SettingsRounded,
} from '@mui/icons-material'
import { Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material'

import { DateTime } from 'luxon'

import { DynamicSelect } from '@common/components/Selects'
import { useSmallScreen } from '@common/utils'
import propertyUrls from '@portal/pages/Properties/urls'
import unitUrls from '@portal/pages/Units/urls'

import HeaderItem from './HeaderItem'

function DeviceSelector({
  loading,
  modalCoreData,
  setSelectedData,
  deviceOption,
  ...rest
}) {
  return (
    <DynamicSelect
      getFullEntity
      disableClearable
      useValueInRequest={false}
      label="Device"
      endpoint="devices"
      value={modalCoreData.device}
      onChange={(device) => {
        setSelectedData({ id: device.id, entity: 'device' })
      }}
      disabled={loading}
      primaryTextAttr="mainMac"
      optionRender={deviceOption}
      variant="outlined"
      size="small"
      sx={{ minWidth: 206 }}
      {...rest}
    />
  )
}

function RefreshButton({ loading, selectedData, setRefreshing, fetchData }) {
  return (
    <Tooltip title="Refresh">
      <span>
        <IconButton
          disabled={loading}
          onClick={() => {
            setRefreshing(true)
            fetchData({
              id: selectedData.id,
              entity: selectedData.entity,
            })
          }}
        >
          <Refresh />
        </IconButton>
      </span>
    </Tooltip>
  )
}

function DateSelectorButton({ loading, formatDateTooltip, setTimeframePopoverAnchor }) {
  return (
    <Tooltip title={formatDateTooltip()}>
      <span>
        <IconButton
          disabled={loading}
          onClick={(event) => setTimeframePopoverAnchor(event.currentTarget)}
        >
          <DateRangeRounded />
        </IconButton>
      </span>
    </Tooltip>
  )
}

function SettingsButton({ loading, setSettingsPopoverAnchor, ...rest }) {
  return (
    <Button
      variant="outlined"
      startIcon={<SettingsRounded />}
      disabled={loading}
      onClick={(event) => setSettingsPopoverAnchor(event.currentTarget)}
      {...rest}
    >
      Settings
    </Button>
  )
}

function SaveScenarioButton({
  loading,
  modalCoreData,
  currentData,
  setScenarioOverlapFormOpen,
  setScenarioFormData,
  buildInitialScenarioFormData,
  ...rest
}) {
  return (
    <Button
      disabled={!modalCoreData.device || loading}
      variant="outlined"
      startIcon={<BookOutlined />}
      onClick={() => {
        if (currentData.scenarios.length > 0) {
          setScenarioOverlapFormOpen(true)
        } else {
          setScenarioFormData(buildInitialScenarioFormData())
        }
      }}
      {...rest}
    >
      Save Scenario
    </Button>
  )
}

function HeaderDataItems({
  modalCoreData,
  selectedData,
  unitDemoModeData,
  formatEventDateRange,
  links,
}) {
  const { property: propertyLink, unit: unitLink } = links

  return (
    <>
      <HeaderItem
        label="Property"
        value={modalCoreData[selectedData.entity]?.propertyName ?? 'N/A'}
        linkTo={propertyLink}
      />
      <HeaderItem
        label="Unit"
        value={modalCoreData[selectedData.entity]?.unitName ?? 'N/A'}
        linkTo={unitLink}
        HeaderIcon={unitDemoModeData ? BuildCircleOutlined : null}
        headerIconHint={unitDemoModeData?.tooltip}
        color={unitDemoModeData ? 'warning.main' : null}
      />
      {modalCoreData?.event && (
        <HeaderItem label="Event Range" value={formatEventDateRange()} />
      )}
    </>
  )
}

function DesktopHeader({
  loading,
  data,
  stateFunctions,
  otherFunctions,
  links,
  isAtLeastDataScience,
  deviceOption,
}) {
  const { chartData, currentData, modalCoreData, selectedData, unitDemoModeData } = data
  const {
    fetchData,
    formatEventDateRange,
    formatDateTooltip,
    buildInitialScenarioFormData,
  } = otherFunctions

  const {
    setSelectedData,
    setRefreshing,
    setTimeframePopoverAnchor,
    setSettingsPopoverAnchor,
    setScenarioOverlapFormOpen,
    setScenarioFormData,
  } = stateFunctions

  return (
    <Stack direction="row" gap={2} mt={2} alignItems="center" flexWrap="wrap">
      <DeviceSelector
        loading={loading}
        modalCoreData={modalCoreData}
        setSelectedData={setSelectedData}
        deviceOption={deviceOption}
      />
      <HeaderDataItems
        modalCoreData={modalCoreData}
        selectedData={selectedData}
        unitDemoModeData={unitDemoModeData}
        formatEventDateRange={formatEventDateRange}
        links={links}
      />
      {chartData && (
        <RefreshButton
          loading={loading}
          selectedData={selectedData}
          setRefreshing={setRefreshing}
          fetchData={fetchData}
        />
      )}
      {chartData && (
        <DateSelectorButton
          loading={loading}
          formatDateTooltip={formatDateTooltip}
          setTimeframePopoverAnchor={setTimeframePopoverAnchor}
        />
      )}
      {chartData && (
        <SettingsButton
          loading={loading}
          setSettingsPopoverAnchor={setSettingsPopoverAnchor}
        />
      )}
      {chartData && currentData && isAtLeastDataScience && (
        <SaveScenarioButton
          loading={loading}
          modalCoreData={modalCoreData}
          currentData={currentData}
          setScenarioOverlapFormOpen={setScenarioOverlapFormOpen}
          setScenarioFormData={setScenarioFormData}
          buildInitialScenarioFormData={buildInitialScenarioFormData}
        />
      )}
    </Stack>
  )
}

function MobileHeader({
  loading,
  data,
  stateFunctions,
  otherFunctions,
  links,
  isAtLeastDataScience,
  deviceOption,
}) {
  const { chartData, currentData, modalCoreData, selectedData, unitDemoModeData } = data
  const {
    fetchData,
    formatEventDateRange,
    formatDateTooltip,
    buildInitialScenarioFormData,
  } = otherFunctions

  const {
    setSelectedData,
    setRefreshing,
    setTimeframePopoverAnchor,
    setSettingsPopoverAnchor,
    setScenarioOverlapFormOpen,
    setScenarioFormData,
  } = stateFunctions

  return (
    <Stack direction="row" gap={2} mt={2} alignItems="center" flexWrap="wrap">
      <Box display="flex" width={1} gap={1}>
        <DeviceSelector
          fullWidth
          loading={loading}
          modalCoreData={modalCoreData}
          setSelectedData={setSelectedData}
          deviceOption={deviceOption}
        />
        <Box display="flex">
          {chartData && (
            <RefreshButton
              loading={loading}
              selectedData={selectedData}
              setRefreshing={setRefreshing}
              fetchData={fetchData}
            />
          )}
          {chartData && (
            <DateSelectorButton
              loading={loading}
              formatDateTooltip={formatDateTooltip}
              setTimeframePopoverAnchor={setTimeframePopoverAnchor}
            />
          )}
        </Box>
      </Box>

      <HeaderDataItems
        modalCoreData={modalCoreData}
        selectedData={selectedData}
        unitDemoModeData={unitDemoModeData}
        formatEventDateRange={formatEventDateRange}
        links={links}
      />

      <Box display="flex" width={1} gap={2}>
        {chartData && (
          <SettingsButton
            fullWidth
            loading={loading}
            setSettingsPopoverAnchor={setSettingsPopoverAnchor}
          />
        )}
        {chartData && currentData && isAtLeastDataScience && (
          <SaveScenarioButton
            fullWidth
            loading={loading}
            modalCoreData={modalCoreData}
            currentData={currentData}
            setScenarioOverlapFormOpen={setScenarioOverlapFormOpen}
            setScenarioFormData={setScenarioFormData}
            buildInitialScenarioFormData={buildInitialScenarioFormData}
          />
        )}
      </Box>
    </Stack>
  )
}

export default function GrafanaViewHeader({
  loading,
  data,
  stateFunctions,
  otherFunctions,
}) {
  const isSmallScreen = useSmallScreen()
  const { isAtLeastDataScience } = useConnect('selectIsAtLeastDataScience')

  const { modalCoreData, selectedData, selectedTimezone } = data

  const propertyLink = useMemo(() => {
    const propertyId =
      modalCoreData?.[selectedData.entity]?.property ??
      modalCoreData?.[selectedData.entity]?.propertyId
    return propertyId ? propertyUrls.entity.replace(':id', propertyId) : null
  }, [modalCoreData, selectedData])

  const unitLink = useMemo(() => {
    const unitId =
      modalCoreData?.[selectedData.entity]?.unit ??
      modalCoreData?.[selectedData.entity]?.unitId
    return unitId ? unitUrls.entity.replace(':id', unitId) : null
  }, [modalCoreData, selectedData])

  const deviceOption = useCallback(
    (option) => (
      <Box>
        <Typography variant="h6">{option.mainMac}</Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          textTransform="uppercase"
          fontSize="0.7rem"
        >
          {option.propertyName} - {option.unitName}
        </Typography>
      </Box>
    ),
    [],
  )

  const formatEventDateRange = () => {
    const format = 'LLL dd, yyyy hh:mma'
    const formattedStart = DateTime.fromISO(modalCoreData.event.createdOn, {
      zone: selectedTimezone,
    }).toFormat(format)
    const formattedEnd = modalCoreData.event.endTime
      ? DateTime.fromISO(modalCoreData.event.endTime, {
          zone: selectedTimezone,
        }).toFormat(format)
      : 'Ongoing'
    return `${formattedStart} - ${formattedEnd}`
  }

  const Header = isSmallScreen.mobile ? MobileHeader : DesktopHeader

  return (
    <Header
      loading={loading}
      data={data}
      stateFunctions={stateFunctions}
      otherFunctions={{ ...otherFunctions, formatEventDateRange }}
      links={{ property: propertyLink, unit: unitLink }}
      isAtLeastDataScience={isAtLeastDataScience}
      deviceOption={deviceOption}
    />
  )
}
